import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { generateHSLColor } from '@/utils/colors';
import { getInitials } from '@/utils/initials';
import { Typography, TypographyProps } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

export type AvatarSize =
  | 'xsmall'
  | 'small'
  | 'medium'
  | 'large'
  | 'xlarge'
  | 'xxlarge';

export const AVATAR_SIZES: Record<AvatarSize, number> = {
  large: 40,
  medium: 32,
  small: 24,
  xlarge: 48,
  xsmall: 16,
  xxlarge: 124,
};

const AVATAR_TEXT_VARIANT: Record<AvatarSize, TypographyProps['variant']> = {
  large: 'label-md',
  medium: 'label-md',
  small: 'label-sm',
  xlarge: 'label-md',
  xsmall: 'label-md',
  xxlarge: 'label-md',
};

export interface Props {
  backgroundColor: string;
  initials: string;
  size?: AvatarSize;
}

export const Avatar: React.FC<Props> = ({
  backgroundColor,
  initials,
  size = 'large',
}) => {
  const [styles, theme] = useStyles();
  const avatarSize = AVATAR_SIZES[size];

  return (
    <View
      style={[
        styles.container,
        { backgroundColor },
        {
          borderRadius: avatarSize / 2,
          height: avatarSize,
          width: avatarSize,
        },
      ]}
    >
      <Typography
        color={theme.sk.color.text.ondark}
        variant={AVATAR_TEXT_VARIANT[size]}
      >
        {initials}
      </Typography>
    </View>
  );
};

const useStyles = createStyles(() => ({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export const createAvatarProps = (
  name: string,
  seed?: string,
  maxInitials = 1,
): Pick<Props, 'backgroundColor' | 'initials'> => {
  if (!name) {
    return {
      backgroundColor: generateHSLColor(seed || ''),
      initials: 'N/A',
    };
  }

  return {
    backgroundColor: generateHSLColor(seed || name),
    initials: getInitials(name, maxInitials) || 'N/A',
  };
};
